<!--
  The cast, as the owner asked for it: "actor pictures with bubbles, their faces,
  and their names underneath — like literal pictures of them."

  A horizontally scrolling row of circular portraits, the name beneath each, the
  character beneath that when a source states one. Faces come from `cast.ts`
  (Wikidata/Commons first — CC0 data, attributed free images — see docs/CAST.md);
  a person with no photo shows an initials plate, because the plate is `Art`'s
  floor, not a branch: it sits UNDERNEATH the image from the first frame, so an
  image that never loads — deferred by the lazy gate below, or 429'd by
  Wikimedia — still reads as a person, never as a broken box. (That is the
  defect Art's own header records; reusing Art is how this row cannot
  reintroduce it.)

  THE BUBBLES ARE LINKS AGAIN — BUT INWARD. The owner first asked for inert
  images ("I don't want the cast images to actually be clickable links to
  anything") when the only place a face could go was an EXTERNAL source page.
  He has since evolved it: "when we are viewing the casts, I want to be able to
  click on a cast member, and it would open their profile." So each face now
  links to OUR OWN `/person/:key` page (`personHref` — the member's Wikidata
  QID when the work chain resolved one, their name otherwise), never off-app.
  The photo-credit trail moved with the click: the source page still rides
  along as non-navigating `title` text here, and the person page shows the
  credit as a visible link — attribution got stronger, not weaker.

  While the lookup runs, six skeleton circles hold the exact space, so the episode
  list below does not jump when the faces land. A title no source knows renders
  nothing at all.
-->
<script lang="ts">
  import type { MediaKind } from '$types';
  import Art from './Art.svelte';
  import Skeleton from './Skeleton.svelte';
  import { castFor, type CastMember } from './cast';
  import { personHref } from './nav.svelte';

  interface Props {
    kind: MediaKind;
    imdbId: string;
    /** Cinemeta's billed names — ordering hint, and the last-resort lookup. */
    names?: string[];
    /** Cinemeta's TMDB id; used only when the owner has pasted a key. */
    moviedbId?: number | undefined;
  }
  let { kind, imdbId, names = [], moviedbId }: Props = $props();

  let members = $state<CastMember[]>([]);
  let loading = $state(true);

  /**
   * The REAL lazy gate. `loading="lazy"` does not defer here — measured: all 12
   * offscreen faces of a phone-width row were requested the moment the row
   * mounted, because Chromium's lazy distance is generous enough to swallow a
   * horizontal rail whole. So a bubble only receives its src once it is within
   * 200px of actually being seen; the observer's default root respects both the
   * row's scrollport and the page viewport.
   */
  let shown = $state<Record<number, boolean>>({});
  let io: IntersectionObserver | null = null;

  function lazyface(node: HTMLElement, index: number): { destroy(): void } {
    if (typeof IntersectionObserver === 'undefined') {
      shown[index] = true;
      return { destroy() {} };
    }
    io ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          shown[Number((entry.target as HTMLElement).dataset['idx'])] = true;
          io?.unobserve(entry.target);
        }
      },
      { rootMargin: '200px' }
    );
    node.dataset['idx'] = String(index);
    io.observe(node);
    return {
      destroy() {
        io?.unobserve(node);
      }
    };
  }

  $effect(() => () => io?.disconnect());

  $effect(() => {
    const id = imdbId;
    const k = kind;
    const billed = names;
    const mdb = moviedbId;
    if (!id) return;

    const abort = new AbortController();
    loading = true;
    members = [];
    shown = {};

    void castFor({ kind: k, imdbId: id, names: billed, ...(mdb !== undefined ? { moviedbId: mdb } : {}) }, abort.signal)
      .then((found) => {
        if (abort.signal.aborted) return;
        members = found;
        loading = false;
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        members = [];
        loading = false;
      });

    return () => abort.abort();
  });
</script>

{#if loading}
  <section class="cast" aria-hidden="true">
    <h2>Cast</h2>
    <ul class="row">
      {#each { length: 6 }, i (i)}
        <li>
          <Skeleton width="var(--face)" height="var(--face)" radius="50%" />
          <div class="name-skeleton"><Skeleton width="80%" height="0.7rem" radius="4px" /></div>
        </li>
      {/each}
    </ul>
  </section>
{:else if members.length}
  <section class="cast">
    <h2>Cast</h2>
    <!-- A rail of faces: Left/Right walk the cast, Up/Down leave it. -->
    <ul class="row" data-dpad="row" aria-label="Cast">
      {#each members as member, i (`${i}:${member.name}`)}
        <li use:lazyface={i}>
          <!-- An INTERNAL link to the person's own page (see the header).
               Photo credit still rides along as title text (the source page
               URL) so the licence trail isn't erased from this surface. -->
          <a
            class="face"
            href={personHref(member)}
            title={member.creditUrl ? `${member.name} — photo: ${member.creditUrl}` : member.name}
            aria-label={member.name}
          >
            <Art
              src={shown[i] ? member.photo : undefined}
              srcset={shown[i] ? member.srcset : undefined}
              sizes="(min-width: 700px) 88px, 76px"
              alt=""
              label={member.name}
              ratio="1 / 1"
              rounded={false}
            />
          </a>
          <p class="name">{member.name}</p>
          {#if member.character}
            <p class="char">{member.character}</p>
          {/if}
        </li>
      {/each}
    </ul>
  </section>
{/if}

<style>
  .cast {
    /* One face at each size; everything below derives from it. 76px ≥ the 44px
       hit floor with room to spare, and 16 faces of Commons 120/250px srcset stay
       inside the row's byte budget (docs/CAST.md § H). */
    --face: 76px;
    margin-top: 20px;
  }

  h2 {
    padding: 0 var(--page-pad);
    margin-bottom: 10px;
    font-size: var(--text-xs);
    font-weight: var(--weight-semi);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wide);
    color: var(--text-faint);
  }

  .row {
    list-style: none;
    margin: 0;
    display: flex;
    gap: 14px;
    padding: 2px var(--page-pad) 4px;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .row::-webkit-scrollbar {
    display: none;
  }

  li {
    flex: none;
    width: var(--face);
    text-align: center;
  }

  .face {
    display: block;
    width: var(--face);
    height: var(--face);
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid var(--line);
    /* Safari needs the clip to survive the img's own compositing layer. Standard
       form only — the build adds the -webkit- twin, and hand-prefixing is what
       cost every glass surface its blur (docs/STATUS.md, the minifier incident). */
    mask-image: radial-gradient(white, black);
  }
  /* Selection is the ring colour, never the accent (DESIGN-LANGUAGE §2 rule 2).
     Drawn INSIDE the circle (negative offset): the Safari mask above clips
     anything painted outside the border box, so an outer ring would vanish. */
  .face:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: -2px;
  }

  .name-skeleton {
    display: flex;
    justify-content: center;
    margin-top: 7px;
  }

  .name {
    margin-top: 7px;
    font-size: var(--text-xs);
    line-height: 1.25;
    color: var(--text-dim);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
  }
  .char {
    margin-top: 2px;
    font-size: var(--text-2xs);
    line-height: 1.3;
    color: var(--text-faint);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
  }

  @media (min-width: 700px) {
    .cast {
      --face: 88px;
    }
  }
</style>
